import React, { useEffect, useState } from 'react';
import {
  Button,
  CharacterCount,
  Checkboxes,
  Fieldset,
  ErrorSummary,
} from 'govuk-react-jsx';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import {
  getLandlordChallengeDescription,
  setLandlordChallengeDescription,
  getLandlordChallenges,
  setLandlordChallenges,
} from '../../features/application/applicationJourneySlice';
import { challangeInstallation, getAccount } from '../../firebase';
import { getSubmittedApplicationId } from '../../features/dashboard/dashboardSlice';
import useTitle from '../../hooks/useTitle';

const ChallangeInstallation = () => {
  const [errorList, setErrorList] = useState([]);
  const [errorCheckbox, setErrorCheckbox] = useState('');
  const [errorCharCount, setErrorCharCount] = useState('');
  const landlordChallengeDescriptionRedux = useSelector(
    getLandlordChallengeDescription
  );
  const [submitChallengeBtn, setSubmitChallengeBtn] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const landlordChallengesRedux = useSelector(getLandlordChallenges);
  const applicationIdRedux = useSelector(getSubmittedApplicationId);

  useTitle(
    'landlordApp.ChallengeInstallation.s13',
    'Why are you challenging this installation? - OZEV Electric Vehicle Chargepoint Grants - GOV.UK'
  );

  const onCharacterCountChange = (e) => {
    dispatch(setLandlordChallengeDescription(e.target.value));
  };
  const [challengeSubmittedLink, setChallengeSubmittedLink] = useState(
    '/landlordApp/challenge-submitted'
  );

  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;

  const welshTranslator = (transaltionProperty, defaultValue) => {
    return copyProvider.getCopy(transaltionProperty, defaultValue);
  };

  const checkboxDecorator = (transaltionProperty, defaultValue, idValue) => {
    return {
      children: welshTranslator(transaltionProperty, defaultValue),
      value: defaultValue,
      checked: landlordChallengesRedux?.includes(defaultValue),
      id: idValue,
    };
  };

  useEffect(() => {
    dispatch(
      setLandlordChallenges({
        value: [],
        checked: false,
      })
    );
    dispatch(setLandlordChallengeDescription(''));
  }, []);

  const handleLandlordChallengesSelection = (event) => {
    dispatch(
      setLandlordChallenges({
        value: event.target.value,
        checked: event.target.checked,
      })
    );
  };

  // NB: error messages
  const checkboxErrorMsg = {
    children: welshTranslator(
      'landlordApp.ChallengeInstallation.s10',
      'Select a reason for why you want to challenge this application'
    ),
  };
  const charCountBlankError = {
    children: welshTranslator(
      'landlordApp.ChallengeInstallation.s11',
      'Enter a summary of what was wrong with the application'
    ),
  };
  const charCountExeededError = {
    children: welshTranslator(
      'landlordApp.ChallengeInstallation.s12',
      'Your summary must be 500 characters or less'
    ),
  };

  const handleButtonClick = () => {
    const errorListArr = [];
    let hasCheckboxError = false;
    let hasDescriptionErrors = false;

    // NB: if checkbox is not selected
    if (landlordChallengesRedux.length < 1) {
      setErrorCheckbox(checkboxErrorMsg);
      errorListArr.push({ ...checkboxErrorMsg, href: '#cb_1' });
      hasCheckboxError = true;
    } else {
      setErrorCheckbox(null);
      hasCheckboxError = false;
    }

    const descriptionLength = landlordChallengeDescriptionRedux?.length;
    if (descriptionLength === 0 || descriptionLength > 500) {
      let charCountError;
      if (descriptionLength === 0) {
        charCountError = charCountBlankError;
      }
      if (descriptionLength > 500) {
        charCountError = charCountExeededError;
      }
      setErrorCharCount(charCountError);
      errorListArr.push({ ...charCountError, href: '#moreDetail' });
      hasDescriptionErrors = true;
    } else {
      setErrorCharCount(null);
      hasDescriptionErrors = false;
    }

    if (!hasCheckboxError && !hasDescriptionErrors) {
      setErrorList([]);
      if (!submitChallengeBtn) {
        setSubmitChallengeBtn(true);
        challangeInstallation({
          applicationId: applicationIdRedux,
          landlordChallenges: landlordChallengesRedux,
          landlordChallengeDescription: landlordChallengeDescriptionRedux,
        }).then(() => {
          history.push(challengeSubmittedLink);
        });
      }
    } else {
      setErrorList(errorListArr);
    }
  };

  useEffect(() => {
    getAccount()
      .then((response) => {
        if (response.data?.account_type === 'Property Management Company') {
          setChallengeSubmittedLink('/pmcApp/challenge-submitted');
        } else if (response.data?.account_type === 'Landlord') {
          setChallengeSubmittedLink('/landlordApp/challenge-submitted');
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.debug('Get Account error:', err.message);
        throw new Error(err);
      });
  });

  return (
    <>
      {errorList?.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <h1 className="govuk-heading-xl">
        {welshTranslator(
          'landlordApp.ChallengeInstallation.s0',
          'Why are you challenging this installation?'
        )}
      </h1>
      <Fieldset>
        <Checkboxes
          errorMessage={errorCheckbox}
          onChange={(event) => handleLandlordChallengesSelection(event)}
          idPrefix="landlordChallenges"
          hint={{
            children: welshTranslator(
              'landlordApp.ChallengeInstallation.s6',
              'Select all that apply'
            ),
          }}
          items={[
            checkboxDecorator(
              'landlordApp.ChallengeInstallation.s1',
              'The photograghs provided are wrong',
              'cb_1'
            ),
            checkboxDecorator(
              'landlordApp.ChallengeInstallation.s2',
              'The invoice is wrong',
              'cb_2'
            ),
            checkboxDecorator(
              'landlordApp.ChallengeInstallation.s3',
              'The total amount does not match what was paid',
              'cb_3'
            ),
            checkboxDecorator(
              'landlordApp.ChallengeInstallation.s4',
              'The chargepoint information does not match what was installed',
              'cb_4'
            ),
            checkboxDecorator(
              'landlordApp.ChallengeInstallation.s5',
              'Other',
              'cb_5'
            ),
          ]}
        />
      </Fieldset>
      <h2
        className="govuk-heading-m"
        style={{ paddingBottom: '0px', marginBottom: '7px' }}
      >
        {welshTranslator(
          'landlordApp.ChallengeInstallation.s7',
          'Can you provide more detail?'
        )}
      </h2>
      <CharacterCount
        errorMessage={errorCharCount}
        hint={{
          children: welshTranslator(
            'landlordApp.ChallengeInstallation.s8',
            'Provide details about what was wrong with the application.'
          ),
        }}
        value={landlordChallengeDescriptionRedux}
        onChange={onCharacterCountChange}
        className="govuk-textarea govuk-js-character-count"
        id="moreDetail"
        rows={7}
        maxlength={500}
        name="moreDetail"
      />
      <Button onClick={handleButtonClick} disabled={submitChallengeBtn}>
        {submitChallengeBtn
          ? welshTranslator(
              'landlordApp.ChallengeInstallation.s9',
              'Submit challenge'
            )
          : welshTranslator(
              'landlordApp.ChallengeInstallation.s9',
              'Submit challenge'
            )}
      </Button>
    </>
  );
};

export default ChallangeInstallation;
